import React, { createContext, useContext, useState, useCallback, useMemo } from "react";

const ToastContext = createContext(null);

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const show = useCallback((opts) => {
    const id = Math.random().toString(36).slice(2);
    const toast = {
      id,
      title: opts.title || "",
      description: opts.description || "",
      variant: opts.variant || "default", // default | success | error | warning
      duration: opts.duration ?? 3500,
    };
    setToasts((prev) => [...prev, toast]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, toast.duration);
  }, []);

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const variantStyles = {
    default: "bg-slate-800 border-slate-700 text-slate-100",
    success: "bg-emerald-500/15 border-emerald-500/40 text-emerald-100",
    error: "bg-rose-500/15 border-rose-500/40 text-rose-100",
    warning: "bg-amber-500/15 border-amber-500/40 text-amber-100",
  };

  const icons = {
    default: "fa-circle-info",
    success: "fa-circle-check",
    error: "fa-circle-exclamation",
    warning: "fa-triangle-exclamation",
  };

  const value = useMemo(() => ({ show, dismiss }), [show, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        data-testid="toast-stack"
        className="fixed top-4 right-4 left-4 sm:left-auto sm:max-w-sm z-[9999] flex flex-col gap-2 pointer-events-none"
      >
        {toasts.map((t) => (
          <div
            key={t.id}
            data-testid={`toast-${t.variant}`}
            className={`toast-enter pointer-events-auto rounded-xl border px-4 py-3 shadow-2xl ${variantStyles[t.variant]} flex items-start gap-3 backdrop-blur`}
          >
            <i className={`fas ${icons[t.variant]} mt-0.5`}></i>
            <div className="flex-1 text-sm">
              {t.title && <div className="font-semibold">{t.title}</div>}
              {t.description && <div className="text-xs opacity-90 mt-0.5 leading-relaxed">{t.description}</div>}
            </div>
            <button
              onClick={() => dismiss(t.id)}
              className="opacity-60 hover:opacity-100 text-xs"
              data-testid="toast-close"
              aria-label="Close"
            >
              <i className="fas fa-xmark"></i>
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be inside ToastProvider");
  return ctx;
}
